import React, { useState, useEffect } from "react";
import {
  Button,
  FormControl,
  Typography,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormLabel,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { UserKycChange, GetUserStatusTable } from "../redux/Actions/userStatusService";

const UserKycModal = ({
  openUserModalKyc,
  setopenUserModalKyc,
  selectedUser,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedStatus, setSelectedStatus] = useState("");
  const [submitError, setSubmitError] = useState(false);
  const [allowedStatusOptions, setAllowedStatusOptions] = useState(["APPROVED", "REJECTED"]);
  const [selectedCustodian, setSelectedCustodian] = useState(null);
  const completedCustodians = selectedUser?.completedCustodians || [];

  // Helper function to normalize custodian names for comparison
  const normalizeCustodianName = (name) => {
    if (!name) return '';
    return name.toUpperCase().replace(/\s+/g, '_');
  };

  // Allowed custodians list (normalized). Include synonyms such as INTERACTIVE BROKERS -> IBKR.
  const allowedCustodians = ['COINBASE', 'IBKR', 'CHARLES_SCHWAB', 'INTERACTIVE_BROKERS'];
  
  // Check if a custodian is in the allowed list (handles both space and underscore formats)
  const isAllowedCustodian = (custodianName) => {
    if (!custodianName) return false;
    const normalized = normalizeCustodianName(custodianName);
    return allowedCustodians.includes(normalized);
  };

  useEffect(() => {
    if (selectedUser) {
      const custodians = selectedUser?.completedCustodians || [];
      // Reset states when selectedUser changes
      setSelectedStatus(selectedUser.kyc?.status || "");
      setSubmitError(false);
      setAllowedStatusOptions(["APPROVED", "REJECTED"]); // Default options

      // If there's only one completed custodian, select it automatically
      if (custodians.length === 1) {
        setSelectedCustodian(custodians[0]);
      } else {
        setSelectedCustodian(null); // Reset selection for multiple custodians
      }
    }
  }, [selectedUser]);

  const handleClose = () => {
    setopenUserModalKyc(false);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedStatus) {
      setSubmitError(true);
      return;
    }

    if (allowedStatusOptions.includes(selectedStatus)) {
      if(selectedUser.kyc != null) {
        const statusObj = {
          _id: selectedUser.kyc?._id,
          status: selectedStatus,
          custodians: selectedCustodian ? [selectedCustodian.custodian] : []
        };
        
        try {
          const response = await dispatch(UserKycChange(statusObj));
          // Check if the status is APPROVED and API call was successful
          if (selectedStatus === "APPROVED" && response?.payload?.statusCode === 200) {
            navigate(`/user/${selectedUser?.id}`);
          }
          
          dispatch(GetUserStatusTable());
          handleClose();
        } catch (error) {
          console.error('Error updating KYC status:', error);
          alert("Failed to update KYC status. Please try again.");
        }
      } else {
        alert("Kyc Status Not Found");
      }
    }
  };

  return (
    <Dialog open={openUserModalKyc} onClose={handleClose}>
      <DialogTitle style={{width:"300px"}}>Update KYC Status</DialogTitle>
      <DialogContent>
        {completedCustodians.length === 1 ? (
          // Show single custodian name
          <Typography variant="subtitle1" style={{ marginBottom: '20px' }}>
            Custodian: {completedCustodians[0].custodian}
          </Typography>
        ) : (
          // Show dropdown for multiple custodians
          <FormControl fullWidth style={{ marginBottom: '20px' }}>
            <FormLabel 
              component="legend" 
              style={{ 
                marginBottom: '8px',
                fontSize: '16px',
                color: 'rgba(0, 0, 0, 0.87)' 
              }}
            >
              Select Custodian
            </FormLabel>
            <Select
              value={selectedCustodian?.custodian || ''}
              onChange={(e) => {
                const selected = completedCustodians.find(
                  c => c.custodian === e.target.value
                );
                setSelectedCustodian(selected);
              }}
              displayEmpty
              placeholder="Select Custodian"
            >
              {completedCustodians
                .filter(custodian => isAllowedCustodian(custodian.custodian))
                .length > 0 ? (
                  completedCustodians
                    .filter(custodian => isAllowedCustodian(custodian.custodian))
                    .map((custodian, index) => (
                      <MenuItem key={index} value={custodian.custodian}>
                        {custodian.custodian}
                      </MenuItem>
                    ))
                ) : (
                  <MenuItem disabled value="">
                    No custodians available
                  </MenuItem>
                )}
            </Select>
          </FormControl>
        )}

        <FormControl fullWidth style={{ marginTop: 20 }}>
          {allowedStatusOptions.map((option) => (
            <FormControlLabel
              key={option}
              control={
                <Checkbox
                  checked={selectedStatus === option}
                  onChange={handleStatusChange}
                  value={option}
                />
              }
              label={option}
            />
          ))}
          {submitError && (
            <Typography variant="caption" color="error">
              Please choose a status before submitting.
            </Typography>
          )}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          disabled={!selectedCustodian || !selectedStatus || !isAllowedCustodian(selectedCustodian.custodian)}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserKycModal;
